import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { MutationRefetchQueries, ValidationError } from "~/common/types";
import {
  BootstrapDataImportProjectDocument,
  type BootstrapDataImportProjectMutation,
  type BootstrapDataImportProjectMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { BootstrapFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface Props {
  refetchQueriesOnBootstrap: MutationRefetchQueries;
  onSuccess: () => void;
  onValidationError: (error: Pick<ValidationError, "fields">) => void;
}

export function useBootstrapDataImportProjectForm({ refetchQueriesOnBootstrap, onSuccess, onValidationError }: Props) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const { tenantId } = useTenant();
  const [bootstrapDataImportProject] = useMutation<
    BootstrapDataImportProjectMutation,
    BootstrapDataImportProjectMutationVariables
  >(BootstrapDataImportProjectDocument, {
    refetchQueries: refetchQueriesOnBootstrap,
  });

  const submit = useCallback(
    async (data: BootstrapFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const resp = await bootstrapDataImportProject({
          variables: {
            input: {
              tenantId,
              name: data.name,
              year: data.year ? Number(data.year) : null,
              userIds: data.users.map((user) => user.id),
              referenceIds: data.references.map((reference) => reference.id),
              dataSourceTemplateId: data.dataSourceTemplate?.id ?? null,
              dataSourcePropertySchemas: data.propertySchemaAssociations,
              documentUrl: data.documentUrl,
              description: data.description,
            },
          },
        });

        switch (resp.data?.cnutBootstrapDataImportProject.__typename) {
          case "DataImportProjectBootstrapOutput":
            toast.success(t("feature.common.createdSuccessfully", { model: "dataImportProject" }));
            onSuccess();
            break;
          case "ValidationError":
            onValidationError(resp.data.cnutBootstrapDataImportProject);
            break;
          default:
            throw new Error("Invalid response type");
        }
      } catch (e) {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
      } finally {
        // Dismiss here rather than per-branch: the error path used to leave the
        // progress notification on screen.
        toast.dismiss(progressToastId);
        setProcessing(false);
      }
    },
    [tenantId, bootstrapDataImportProject, onSuccess, onValidationError, t],
  );

  return {
    submit,
    processing,
  };
}
